import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Kanban,
  KanbanCardBody,
  KanbanStageLabel,
  KanbanTag,
  Modal,
  ModalActions,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  PersonMark,
  Table,
  TextArea,
  Toolbar,
  type FilterFieldSlot,
  type KanbanStage,
} from '@evogard/ui';
import type { ClaimDeadlineFilterValue, PeripheralSummary } from '@evogard/contract';
import {
  PERIPHERAL_STAGES,
  PERIPHERAL_STAGE_COLORS,
  PERIPHERAL_WORK_STAGES,
  analysisHoldLabel,
  count,
  formatInstant,
  isPeripheralWorkStage,
  peripheralPhaseLabel,
  peripheralSituationLabel,
  type PeripheralStage,
} from '@evogard/domain';
import { useCollapsedStages } from '../../../lib/collapsed-stages.js';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { DataCell } from '../DataCell.js';
import { HoldToggle } from '../HoldToggle.js';
import { ErpVehicleField, type VehicleEligibility } from '../claim/ErpVehicleField.js';
import { PeripheralDeskModal } from './PeripheralDeskModal.js';
import { ClaimLabelTags } from '../claim/ClaimLabels.js';
import { ClaimDeadlineTags } from '../claim/ClaimDeadlines.js';
import {
  fetchPeripheralPage,
  useCreatePeripheral,
  usePeripheralList,
  usePeripheralPermissions,
  useVehicleCoverage,
  type PeripheralFilter,
  usePeripheralLabelOptions,
  usePeripheralSlaSummary,
} from './usePeripherals.js';

type View = 'quadro' | 'tabela';

/** Quantos cartões cada coluna do quadro pede por vez. */
const PAGE_SIZE = 50;

/**
 * Sinistros › Periféricos (08/10/2026) — os acionamentos de peças e serviços
 * periféricos do veículo, em quadro ou em tabela, com a mesma busca e os
 * mesmos filtros nas duas (`INSTRUÇÕES_BD_SINISTRO.md` §5.8).
 *
 * O quadro tem as 7 etapas de trabalho; o acionamento que terminou —
 * Concluídos, Desistência, Negados — sai dele e fica na tabela, como no
 * sinistro. O cartão não se arrasta: mover pede o formulário da etapa, e isso
 * mora na mesa, que o clique abre.
 *
 * **Aqui há "Abrir acionamento"**, ao contrário da Análise: o acionamento de
 * Periféricos não nasce de um aviso de sinistro.
 */
export function PeripheralsScreen({ initialOpen }: { initialOpen?: number } = {}) {
  const { data: context } = useSessionContext();
  const { canCreate } = usePeripheralPermissions();
  const [view, setView] = useState<View>('quadro');
  const [opened, setOpened] = useState<number | null>(initialOpen ?? null);
  const [creating, setCreating] = useState(false);

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [stage, setStage] = useState<PeripheralStage | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  /* O botão Parados: os que não estão parados, ou só os parados — nunca os dois. */
  const [onHold, setOnHold] = useState(false);
  const [labelId, setLabelId] = useState('');
  const [deadline, setDeadline] = useState<ClaimDeadlineFilterValue | ''>('');
  /* O filtro oferece as etiquetas dos setores de quem usa — as que o card mostra. */
  const labelSectors = usePeripheralLabelOptions();
  const labelOptions = (labelSectors.data ?? []).flatMap((s) =>
    s.labels.map((l) => ({
      value: String(l.id),
      label: l.name,
      group: s.departmentName,
    })),
  );
  /* Quantos estão com o SLA expirado em cada etapa — o selo do cabeçalho da coluna. */
  const sla = usePeripheralSlaSummary(opened ?? 0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const filter: PeripheralFilter = {
    search: term,
    stage: stage || undefined,
    includeArchived,
    onHold,
    labelId: labelId ? Number(labelId) : undefined,
    deadline: deadline || undefined,
  };
  const query = usePeripheralList(filter, page, perPage);
  const result = query.data;
  const hasFilter =
    term.trim() !== '' ||
    stage !== '' ||
    includeArchived ||
    onHold ||
    labelId !== '' ||
    deadline !== '';

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'stage',
      label: 'Fase',
      placeholder: 'Todas',
      options: PERIPHERAL_STAGES.map((code) => ({
        value: code,
        label: peripheralPhaseLabel(code),
        group: isPeripheralWorkStage(code) ? 'Etapas' : 'Desfechos',
      })),
    },
    {
      type: 'selecao',
      name: 'deadline',
      label: 'Prazo',
      placeholder: 'Todos',
      /* O SLA EXPIRA e o card VENCE — cada relógio com a sua palavra, como no sinistro. */
      options: [
        { value: 'sla_expirado', label: 'SLA da etapa expirado', group: 'SLA da etapa' },
        {
          value: 'sla_expira_em_breve',
          label: 'SLA expira em breve',
          group: 'SLA da etapa',
        },
        { value: 'vencido', label: 'Vencidos', group: 'Vencimento' },
        {
          value: 'vence_em_breve',
          label: 'Vencem nas próximas 24h',
          group: 'Vencimento',
        },
        { value: 'sem_vencimento', label: 'Sem vencimento', group: 'Vencimento' },
      ],
    },
    {
      type: 'selecao',
      name: 'label',
      label: 'Etiqueta',
      placeholder: 'Todas',
      options: labelOptions,
    },
    {
      type: 'selecao',
      name: 'archived',
      label: 'Arquivados',
      placeholder: 'Esconder',
      options: [{ value: 'sim', label: 'Mostrar também os arquivados' }],
    },
  ];
  const searchSlot = {
    label: 'Pesquisar acionamentos de Periféricos',
    placeholder: 'Número, placa, código do veículo ou nome do associado',
    value: search,
    onChange: (value: string) => {
      setSearch(value);
      setPage(1);
    },
  };
  const filtersSlot = (
    <>
      <HoldToggle
        held={onHold}
        onChange={(held) => {
          setOnHold(held);
          setPage(1);
        }}
      />
      <Filters
        values={{
          stage,
          deadline,
          label: labelId,
          archived: includeArchived ? 'sim' : '',
        }}
        fields={fields}
        onApply={(values) => {
          setStage(String(values.stage ?? '') as PeripheralStage | '');
          setDeadline(String(values.deadline ?? '') as ClaimDeadlineFilterValue | '');
          setLabelId(String(values.label ?? ''));
          setIncludeArchived(values.archived === 'sim');
          setPage(1);
        }}
      />
    </>
  );

  /*
   * No quadro, a fase escolhida no filtro fica aberta e as outras recolhem em
   * faixa (28/10/2026) — até então ela virava a única coluna. O desfecho não
   * tem coluna, e o quadro diz isso no lugar das colunas.
   */
  const { collapsed, onCollapse } = useCollapsedStages(
    'claims.perifericos',
    PERIPHERAL_WORK_STAGES,
    stage || undefined,
  );
  /* O recorte do quadro: tudo do filtro menos a fase, que só abre uma coluna. */
  const { stage: _stage, ...boardFilter } = filter;
  const stages: KanbanStage[] = PERIPHERAL_WORK_STAGES.map((code) => {
    const expired = sla.data?.find((s) => s.phase === code)?.slaExpired ?? 0;
    return {
      id: code,
      name: peripheralPhaseLabel(code),
      color: PERIPHERAL_STAGE_COLORS[code],
      ...(expired > 0 && {
        alert: expired === 1 ? '1 expirado' : `${expired} expirados`,
      }),
    };
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Periféricos"
        actions={
          <>
            <Button
              variant="contorno"
              icon={view === 'quadro' ? 'documento' : 'quadro'}
              onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
            >
              {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
            </Button>
            {canCreate && (
              <Button icon="mais" onClick={() => setCreating(true)}>
                Abrir acionamento
              </Button>
            )}
          </>
        }
      >
        Os acionamentos de para-brisa, farol, retrovisor e lanterna de{' '}
        {context?.currentTenant.nome}, por fase.
      </PageHeader>

      {view === 'quadro' ? (
        stage && !isPeripheralWorkStage(stage) ? (
          <Alert tone="informacao">
            {peripheralPhaseLabel(stage)} é desfecho, e o quadro só tem as etapas de
            trabalho. Veja esses acionamentos na tabela.
          </Alert>
        ) : (
          <Kanban<PeripheralSummary>
            label="Quadro de Periféricos"
            stages={stages}
            toolbar={<Toolbar search={searchSlot} toolbar={filtersSlot} />}
            cardId={(p) => String(p.id)}
            cardLabel={(p) => `Acionamento #${p.id}`}
            loadPage={async (phase, cursor) => {
              const n = cursor === null ? 1 : Number(cursor);
              const page = await fetchPeripheralPage(
                { ...boardFilter, stage: phase as PeripheralStage },
                n,
                PAGE_SIZE,
              );
              return {
                items: page.items,
                next: n * PAGE_SIZE < page.total ? String(n + 1) : null,
                total: page.total,
              };
            }}
            /* A faixa recolhida pede só o total: uma página de um acionamento. */
            countStage={async (phase) =>
              (
                await fetchPeripheralPage(
                  { ...boardFilter, stage: phase as PeripheralStage },
                  1,
                  1,
                )
              ).total
            }
            collapsed={collapsed}
            onCollapse={onCollapse}
            scrollToStage={stage || undefined}
            reloadKey={JSON.stringify([context?.currentTenant.id, boardFilter, opened])}
            renderCard={(p) => <PeripheralCard peripheral={p} />}
            onOpenCard={(p) => setOpened(p.id)}
            emptyColumnText="Nenhum acionamento nesta fase."
          />
        )
      ) : (
        <>
          {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
          {(!query.isError || result) && (
            <Card
              title={
                result === undefined
                  ? 'Carregando…'
                  : count(result.total, 'acionamento', 'acionamentos')
              }
              descricao={includeArchived ? 'Ativos e arquivados.' : 'Só os ativos.'}
              bleed
            >
              <Table
                paginate={false}
                search={searchSlot}
                toolbar={filtersSlot}
                label="Acionamentos de Periféricos"
                items={result?.items ?? []}
                loading={query.isLoading}
                skeletonRows={perPage}
                rowKey={(p) => String(p.id)}
                rowTitle={(p) => `Acionamento #${p.id}`}
                rowSubtitle={positionOf}
                rowIcon="escudo"
                rowActions={(p) => [
                  { label: 'Abrir', icon: 'olho', run: () => setOpened(p.id) },
                ]}
                empty={
                  hasFilter
                    ? 'Nenhum acionamento com esses filtros.'
                    : 'Nenhum acionamento de Periféricos ainda. Todo acionamento nasce na Entrada, pelo botão "Abrir acionamento".'
                }
                columns={[
                  {
                    title: 'Acionamento',
                    alignment: 'inicio' as const,
                    minWidth: '9rem',
                    cell: (p) => (
                      <DataCell icon="escudo">
                        <span className="font-semibold tabular-nums">#{p.id}</span>
                        {p.archivedAt && (
                          <span className="text-texto-suave">arquivado</span>
                        )}
                      </DataCell>
                    ),
                  },
                  {
                    title: 'Associado',
                    alignment: 'inicio' as const,
                    minWidth: '15rem',
                    cell: (p) => <MemberCell peripheral={p} />,
                  },
                  {
                    title: 'Responsável',
                    alignment: 'inicio' as const,
                    minWidth: '12rem',
                    cell: (p) => (
                      <PersonMark
                        name={p.assignee?.name ?? null}
                        emptyLabel="Sem responsável"
                        className="text-sm"
                      />
                    ),
                  },
                  {
                    title: 'Fase',
                    minWidth: '13rem',
                    cell: (p) => (
                      <KanbanStageLabel
                        stage={{
                          name: positionOf(p),
                          color: isPeripheralWorkStage(p.stage)
                            ? PERIPHERAL_STAGE_COLORS[
                                p.stage as keyof typeof PERIPHERAL_STAGE_COLORS
                              ]
                            : undefined,
                        }}
                      />
                    ),
                  },
                  {
                    title: 'Itens',
                    minWidth: '6rem',
                    cell: (p) => <span className="tabular-nums">{p.itemCount}</span>,
                  },
                  {
                    title: 'Cobertura',
                    minWidth: '7rem',
                    cell: (p) =>
                      p.coveragePercent === null ? (
                        <NotProvided />
                      ) : (
                        <span className="tabular-nums">{p.coveragePercent}%</span>
                      ),
                  },
                  {
                    title: 'Aberto em',
                    minWidth: '10rem',
                    cell: (p) => (
                      <DataCell icon="calendario">
                        <span className="tabular-nums">{formatInstant(p.createdAt)}</span>
                      </DataCell>
                    ),
                  },
                ]}
              />
              {result && (
                <Pagination
                  page={result.page}
                  perPage={result.perPage}
                  total={result.total}
                  onChange={setPage}
                  onPerPageChange={(n) => {
                    setPerPage(n);
                    setPage(1);
                  }}
                  label="acionamentos"
                  className="border-t border-borda-suave px-4 py-3"
                />
              )}
            </Card>
          )}
        </>
      )}

      <OpenPeripheralModal
        open={creating}
        onClose={() => setCreating(false)}
        onOpened={(id) => {
          setCreating(false);
          setOpened(id);
        }}
      />
      <PeripheralDeskModal
        key={opened ?? 'fechado'}
        peripheralId={opened}
        onClose={() => setOpened(null)}
      />
    </Page>
  );
}

/** Onde o acionamento está: a etapa, e a situação ou a parada dentro dela. */
function positionOf(p: PeripheralSummary): string {
  return [
    peripheralPhaseLabel(p.stage),
    p.hold ? `parado: ${analysisHoldLabel(p.hold)}` : null,
    p.situation ? peripheralSituationLabel(p.situation) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O dono e o carro — do núcleo. Sem o carro projetado, o código do SGA, e o aviso. */
function MemberCell({ peripheral: p }: { peripheral: PeripheralSummary }) {
  if (!p.vehicle) {
    return (
      <span
        className="text-sm text-texto-suave"
        title="O código ainda não chegou ao cadastro da casa"
      >
        Veículo {p.vehicleCode} · ainda não no cadastro
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{p.vehicle.memberName ?? <NotProvided />}</span>
      <span className="truncate text-xs text-texto-suave">
        {[p.vehicle.plate, p.vehicle.model].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

function PeripheralCard({ peripheral: p }: { peripheral: PeripheralSummary }) {
  return (
    <KanbanCardBody
      tags={
        <>
          {p.archivedAt && <KanbanTag>Arquivado</KanbanTag>}
          {p.hold && (
            <KanbanTag tone="alerta">Parado: {analysisHoldLabel(p.hold)}</KanbanTag>
          )}
          {p.situation && (
            <KanbanTag tone="progresso">
              {peripheralSituationLabel(p.situation)}
            </KanbanTag>
          )}
          {p.coveragePercent !== null && <KanbanTag>{p.coveragePercent}%</KanbanTag>}
          {/* As etiquetas dos setores de quem olha (15/10/2026), como no sinistro. */}
          <ClaimLabelTags labels={p.labels} />
        </>
      }
      title={
        <>
          <span className="tabular-nums">#{p.id}</span> ·{' '}
          {p.vehicle?.memberName ?? <NotProvided />}
          <span className="block text-xs font-normal text-texto-suave">
            {p.vehicle
              ? [p.vehicle.plate, p.vehicle.model].filter(Boolean).join(' · ')
              : `Veículo ${p.vehicleCode}`}
          </span>
        </>
      }
      detail={count(p.itemCount, 'item', 'itens')}
      detailIcon="documento"
      owner={{ name: p.assignee?.name ?? null, emptyLabel: 'Sem responsável' }}
      alerts={p.dueAt || p.phaseDeadlineAt ? <ClaimDeadlineTags claim={p} /> : undefined}
    />
  );
}

/**
 * Cada veículo da busca com a cobertura de Periféricos do contrato dele — o
 * selo diz o percentual, e o carro sem ela aparece e não se escolhe, dizendo
 * por quê. Função de módulo: o campo a chama como hook.
 */
const useCoverageEligibility: VehicleEligibility = (codes) => {
  const query = useVehicleCoverage(codes);
  return {
    error: query.isError ? query.error.message : null,
    of: (code) => {
      const coverage = query.data?.get(code);
      if (!coverage) return undefined;
      return coverage.kind === 'coberto'
        ? {
            pickable: true,
            note: <Badge tone="sucesso">Periféricos {coverage.percent}%</Badge>,
          }
        : {
            pickable: false,
            note: (
              <span className="flex flex-col items-start gap-1">
                <Badge tone={coverage.kind === 'sem_periferico' ? 'neutro' : 'alerta'}>
                  {coverage.kind === 'sem_periferico'
                    ? 'Sem Periféricos'
                    : 'Cobertura não confirmada'}
                </Badge>
                <span className="text-xs text-texto-suave">{coverage.message}</span>
              </span>
            ),
          };
    },
  };
};

/**
 * Abrir um acionamento: o veículo, procurado no cadastro do ERP, e o que
 * aconteceu. A cobertura vem do contrato do carro (15/10/2026) — só abre
 * para quem tem Periféricos. O resto — responsável e as conferências — é o
 * formulário da Entrada, na mesa que abre em seguida.
 */
function OpenPeripheralModal({
  open,
  onClose,
  onOpened,
}: {
  open: boolean;
  onClose: () => void;
  onOpened: (id: number) => void;
}) {
  const [vehicleCode, setVehicleCode] = useState('');
  const [description, setDescription] = useState('');
  const create = useCreatePeripheral();
  const close = () => {
    setVehicleCode('');
    setDescription('');
    create.reset();
    onClose();
  };
  /* Sem busca (sem `erp.catalog:ver`) o código é digitado: pergunta-se depois da pausa. */
  const chosen = useDebouncedTerm(vehicleCode).trim();
  const coverage = useVehicleCoverage(chosen ? [chosen] : []).data?.get(chosen);
  const ready =
    vehicleCode.trim() === chosen &&
    coverage?.kind === 'coberto' &&
    description.trim() !== '';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close()}
      title="Abrir acionamento de Periféricos"
      descricao="Só para veículo com Periféricos no contrato — a cobertura vem dele. Nasce na Entrada; o responsável e as conferências são o formulário dela."
    >
      <div className="flex flex-col gap-4">
        {create.isError && <Alert tone="erro">{create.error.message}</Alert>}
        <ErpVehicleField
          value={vehicleCode}
          onChange={setVehicleCode}
          useEligibility={useCoverageEligibility}
        />
        <TextArea
          label="Descrição do Ocorrido"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={4000}
          help="O que aconteceu com a peça, como o associado contou."
        />
        <ModalActions>
          <Button variant="secundario" onClick={close}>
            Cancelar
          </Button>
          <Button
            disabled={!ready}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                {
                  vehicleCode: vehicleCode.trim(),
                  occurrenceDescription: description.trim(),
                },
                {
                  onSuccess: (p) => {
                    setVehicleCode('');
                    setDescription('');
                    onOpened(p.id);
                  },
                },
              )
            }
          >
            Abrir acionamento
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
